import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { CalendarClock } from "lucide-react";
import { parseStoredDateTime } from "@/utils/timeUtils";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProfileSidebar } from "@/components/profile/ProfileSidebar";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";

interface ServiceProvider {
  id: number;
  name: string | null;
}

interface Booking {
  id: number;
  service_id: number | null;
  start_time: string | null;
  end_time: string | null;
  status: string | null;
  services: ServiceProvider | null;
  customer_id: string | null;
  note?: string | null;
  customer_name?: string | null;
  customer_email?: string | null;
  customer_phone?: string | null;
  customer_address?: string | null;
}

const MyBookings = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("upcoming");

  useEffect(() => {
    const fetchUserSession = async () => {
      const { data } = await supabase.auth.getSession();
      
      if (!data.session) {
        navigate('/auth');
        return;
      }
      
      fetchBookings(data.session.user.id);
    };

    fetchUserSession();
  }, [navigate]);

  const fetchBookings = async (userId: string) => {
    try {
      setIsLoading(true);
      console.log("Fetching bookings for user:", userId);
      
      // Include ALL bookings, even cancelled/rejected ones
      // They should still appear in user's booking history
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id, 
          service_id, 
          start_time, 
          end_time, 
          status,
          note,
          services:service_id(id, name)
        `)
        .eq('customer_id', userId)
        .not('status', 'in', '("blocked","travel")')
        .order('start_time', { ascending: false });

      if (error) {
        console.error("Supabase error details:", error);
        throw error;
      }
      
      console.log("Bookings data:", data);
      setBookings(data as Booking[] || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load your bookings. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookingClick = (booking: Booking) => {
    navigate(`/booking/${booking.id}/details`);
  };

  const refreshBookings = async () => {
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      fetchBookings(data.session.user.id);
    }
  };

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const pastBookings = bookings.filter(booking => 
    booking.start_time && parseStoredDateTime(booking.start_time) < now
  );
  
  const upcomingBookings = bookings.filter(booking => 
    booking.start_time && parseStoredDateTime(booking.start_time) >= now
  );

  const getStatusLabel = (status: string | null): string => {
    if (!status) return 'New';
    
    switch (status.toLowerCase()) {
      case 'new': return 'New';
      case 'accepted': return 'Accepted';
      case 'declined': return 'Declined';
      case 'cancelled': return 'Cancelled';
      case 'completed': return 'Completed';
      case 'blocked': return 'Blocked';
      case 'travel': return 'Travel';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const getStatusColor = (status: string | null): string => {
    if (!status) return 'text-amber-600';
    
    switch (status.toLowerCase()) {
      case 'new': return 'text-amber-600';
      case 'accepted': return 'text-green-600';
      case 'declined': 
      case 'cancelled': return 'text-red-600';
      case 'completed': return 'text-blue-600';
      case 'blocked': 
      case 'travel': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const renderBookingsList = (bookingsList: Booking[]) => {
    if (bookingsList.length === 0) {
      return (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">No bookings to display.</p>
          </CardContent>
        </Card>
      );
    }

    // Group bookings by active (pending/confirmed) and inactive (cancelled/declined)
    const activeBookings = bookingsList.filter(b => 
      !['declined', 'cancelled'].includes(b.status?.toLowerCase() || '')
    );
    
    const inactiveBookings = bookingsList.filter(b => 
      ['declined', 'cancelled'].includes(b.status?.toLowerCase() || '')
    );
    
    // Render the two groups
    return (
      <>
        {activeBookings.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-6">
            {activeBookings.map((booking) => (
              <Card 
                key={booking.id} 
                className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleBookingClick(booking)}
              >
                <CardHeader className="pb-3">
                  <CardTitle>{booking.services?.name || "Service"}</CardTitle>
                  <CardDescription>
                    {booking.start_time && format(parseStoredDateTime(booking.start_time), "MMMM d, yyyy")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center text-sm gap-2">
                      <CalendarClock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {booking.start_time && format(parseStoredDateTime(booking.start_time), "h:mm a")} - 
                        {booking.end_time && format(parseStoredDateTime(booking.end_time), "h:mm a")}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-muted-foreground">Status:</span>
                      <span className={`text-sm font-medium ${getStatusColor(booking.status)}`}>
                        {getStatusLabel(booking.status)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {inactiveBookings.length > 0 && (
          <>
            {activeBookings.length > 0 && (
              <div className="mb-4 mt-8">
                <h3 className="text-sm font-medium text-gray-500">Cancelled/Declined Bookings</h3>
                <div className="border-t border-gray-100 mt-2"></div>
              </div>
            )}
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 opacity-75">
              {inactiveBookings.map((booking) => (
                <Card 
                  key={booking.id} 
                  className="overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleBookingClick(booking)}
                >
                  <CardHeader className="pb-3">
                    <CardTitle>{booking.services?.name || "Service"}</CardTitle>
                    <CardDescription>
                      {booking.start_time && format(parseStoredDateTime(booking.start_time), "MMMM d, yyyy")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center text-sm gap-2">
                        <CalendarClock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          {booking.start_time && format(parseStoredDateTime(booking.start_time), "h:mm a")} - 
                          {booking.end_time && format(parseStoredDateTime(booking.end_time), "h:mm a")}
                        </span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Status:</span>
                        <span className={`text-sm font-medium ${getStatusColor(booking.status)}`}>
                          {getStatusLabel(booking.status)}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50 flex flex-col w-full">
        {/* Fixed header */}
        <div className="fixed top-0 left-0 right-0 z-40 h-16">
          <Header showSearchAndLocation={false} />
        </div>
        
        {/* Main layout with sidebar - starts after header */}
        <div className="flex flex-1 pt-16">
          <ProfileSidebar />
          
          <SidebarInset>
            {/* Main content with proper spacing to clear header */}
            <main className="flex-1 overflow-auto p-4 md:p-6">
              <div className="max-w-4xl mx-auto">
                <h1 className="text-3xl font-bold mb-4">My Bookings</h1>
                
                {isLoading ? (
                  <div className="flex justify-center items-center h-40">
                    <p>Loading your bookings...</p>
                  </div>
                ) : bookings.length === 0 ? (
                  <Card>
                    <CardContent className="pt-6">
                      <p className="text-center text-muted-foreground">You don't have any bookings yet.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 mb-6">
                      <TabsTrigger value="upcoming">Upcoming Bookings</TabsTrigger>
                      <TabsTrigger value="past">Past Bookings</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="upcoming" className="mt-0 max-w-full overflow-x-hidden">
                      {renderBookingsList(upcomingBookings)}
                    </TabsContent>
                    
                    <TabsContent value="past" className="mt-0 max-w-full overflow-x-hidden">
                      {renderBookingsList(pastBookings)}
                    </TabsContent>
                  </Tabs>
                )}
              </div>
            </main>
          </SidebarInset>
        </div>
        
        <Footer />
      </div>
    </SidebarProvider>
  );
};

export default MyBookings;
